import { useFocusEffect, useLocalSearchParams, useRouter } from 'expo-router';
import { useSQLiteContext } from 'expo-sqlite';
import {
  Calendar,
  CheckSquare,
  ChevronLeft,
  ChevronRight,
  Home as HomeIcon,
  Plus,
  Search as SearchIcon,
  Sparkles,
  Square,
  Trash2,
  X,
} from 'lucide-react-native';
import { ComponentType, useCallback, useMemo, useState } from 'react';
import {
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { InfiniteFooter } from '@/components/InfiniteFooter';
import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import {
  getSubtypeMeta,
  resolveSystemDisplay,
  SYSTEM_CATALOG,
} from '@/data/systems';
import type { SystemRow } from '@/db/types';
import { useConfirm } from '@/components/ConfirmDialog';
import { useInfiniteWindow } from '@/hooks/use-infinite-window';
import { goBackOrTo, goTo } from '@/lib/nav';
import { webTransition } from '@/lib/hover-style';
import { resolveIcon } from '@/lib/rule-icon';
import {
  deleteSystem,
  getActiveProperty,
  getSystemsForProperty,
} from '@/lib/tasks';
import { webNoOutline } from '@/lib/web-style';

/**
 * The "Systems" management page — the dedicated place to find,
 * search, edit, and bulk-manage every system in your home.
 *
 * Why this page exists separate from My Home:
 *   - My Home is a property dashboard (map, profile, summary).
 *     It shows a *preview* of systems so the page stays scannable.
 *   - This page is the deep tool: search by name / kind / subtype,
 *     filter by location and "needs tune" state, multi-select for
 *     bulk delete, and infinite-scroll the full list at large
 *     home portfolios.
 *
 * Surfaced from:
 *   - My Home → "View all systems" CTA at the bottom of the preview lists.
 *   - Settings → Tools → Systems tile (replaces the previous Search tile).
 *   - Quick toolbar items that point at /systems.
 */

type LocationFilter = 'all' | 'inside' | 'outside';

export default function SystemsScreen() {
  const router = useRouter();
  const db = useSQLiteContext();
  const confirm = useConfirm();
  const [all, setAll] = useState<SystemRow[]>([]);
  const [query, setQuery] = useState('');
  // Honor an optional `?loc=inside|outside` deep-link param. Used by the
  // My Home tab's "31 systems" tappable section count so the user lands
  // pre-filtered to the location they tapped instead of seeing All and
  // having to pick a chip.
  const params = useLocalSearchParams<{ loc?: string }>();
  const initialLoc: LocationFilter =
    params.loc === 'inside' || params.loc === 'outside' ? params.loc : 'all';
  const [locFilter, setLocFilter] = useState<LocationFilter>(initialLoc);
  const [tuneOnly, setTuneOnly] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  const fetch = useCallback(async () => {
    const prop = await getActiveProperty(db);
    if (!prop) {
      setAll([]);
      return;
    }
    const rows = await getSystemsForProperty(db, prop.id);
    setAll(rows);
  }, [db]);

  useFocusEffect(useCallback(() => { fetch(); }, [fetch]));

  // Cache the "needs tune" set up front — kind has subtype options AND
  // user hasn't picked one. Drives both the filter chip and the
  // per-row badge below.
  const needsTuneIds = useMemo(() => {
    const ids = new Set<string>();
    for (const sys of all) {
      const meta = SYSTEM_CATALOG[sys.kind];
      if (meta?.subtypes?.length && !sys.subtype) ids.add(sys.id);
    }
    return ids;
  }, [all]);

  const filtered = useMemo(() => {
    let out = all;
    if (locFilter !== 'all') {
      out = out.filter((s) => s.location === locFilter);
    }
    if (tuneOnly) {
      out = out.filter((s) => needsTuneIds.has(s.id));
    }
    const q = query.trim().toLowerCase();
    if (q.length > 0) {
      out = out.filter((s) => {
        const meta = SYSTEM_CATALOG[s.kind];
        const subtypeLabel = getSubtypeMeta(s.kind, s.subtype)?.label ?? '';
        const haystack = [
          s.nickname ?? '',
          meta?.label ?? '',
          s.kind,
          subtypeLabel,
          s.model ?? '',
          s.notes ?? '',
        ]
          .join(' ')
          .toLowerCase();
        return haystack.includes(q);
      });
    }
    return out;
  }, [all, locFilter, tuneOnly, needsTuneIds, query]);

  const win = useInfiniteWindow(filtered, { pageSize: 25, initialSize: 20 });

  const toggleSelected = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const exitSelect = () => {
    setSelectMode(false);
    setSelected(new Set());
  };

  const onBulkDelete = async () => {
    if (selected.size === 0) return;
    const ok = await confirm({
      title: `Delete ${selected.size} system${selected.size === 1 ? '' : 's'}?`,
      message: 'They\'ll be removed along with their reminders. Tasks already scheduled stay scheduled (just untethered).',
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    setBusy(true);
    for (const id of selected) {
      await deleteSystem(db, id);
    }
    setBusy(false);
    exitSelect();
    fetch();
  };

  // Group visible systems by location for the rendered list. Inside
  // first, then Outside — same order users see on My Home.
  const grouped = useMemo(() => {
    const inside: SystemRow[] = [];
    const outside: SystemRow[] = [];
    for (const s of win.visible) {
      if (s.location === 'outside') outside.push(s);
      else inside.push(s);
    }
    return { inside, outside };
  }, [win.visible]);

  const totalTune = needsTuneIds.size;

  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      <View style={styles.topbar}>
        <Pressable
          onPress={() => goBackOrTo(router, '/(tabs)/my-home')}
          style={({ pressed }) => [styles.back, pressed && { opacity: 0.7 }]}
          hitSlop={12}>
          <ChevronLeft size={22} color={Palette.ink} strokeWidth={2} />
        </Pressable>
        <Text style={styles.topbarTitle}>Systems</Text>
        {selectMode ? (
          <Pressable
            onPress={exitSelect}
            style={({ pressed }) => [styles.back, pressed && { opacity: 0.7 }]}
            hitSlop={12}
            accessibilityLabel="Exit selection">
            <X size={20} color={Palette.ink} strokeWidth={2} />
          </Pressable>
        ) : (
          <Pressable
            onPress={() => router.push('/system/new' as never)}
            style={({ pressed }) => [styles.addBtn, pressed && { transform: [{ scale: 0.97 }] }]}
            accessibilityLabel="Add a system">
            <Plus size={18} color={Palette.cream} strokeWidth={2.2} />
          </Pressable>
        )}
      </View>

      <ScrollView
        contentContainerStyle={{ paddingBottom: selectMode ? 110 : 60 }}
        keyboardShouldPersistTaps="handled"
        onScroll={win.onScroll}
        onLayout={win.onLayout}
        scrollEventThrottle={16}
        showsVerticalScrollIndicator={false}>
        <View style={styles.header}>
          {/* Tune CTA rides the TITLE row (Wave 66, user call) — the
              space right of "Your systems" was empty, and hoisting the
              chip keeps the whole header one line shorter. Wraps under
              the title on narrow phones via flexWrap. */}
          <View style={styles.titleRow}>
            <Text style={styles.h1}>Your systems</Text>
            {totalTune > 0 ? (
              <Pressable
                onPress={() => router.push('/profile-setup' as never)}
                style={({ pressed, hovered }) => [
                  styles.tuneCta,
                  webTransition,
                  hovered && { borderColor: Palette.honeyDeep, backgroundColor: '#FBF6EA' },
                  pressed && { transform: [{ scale: 0.99 }] },
                ]}>
                <Sparkles size={16} color={Palette.honeyDeep} strokeWidth={2} />
                <Text style={styles.tuneCtaText}>
                  Tune {totalTune} system{totalTune === 1 ? '' : 's'} — one chip each
                </Text>
                <ChevronRight size={15} color={Palette.honeyDeep} strokeWidth={2} />
              </Pressable>
            ) : null}
          </View>
          <Text style={styles.sub}>
            {all.length} total
            {totalTune > 0 ? ` · ${totalTune} still need a chip pick` : ''}
          </Text>
        </View>

        {/* Search bar */}
        <View style={styles.searchWrap}>
          <View style={styles.searchInner}>
            <SearchIcon size={16} color={Palette.inkFaint} strokeWidth={2} />
            <TextInput
              value={query}
              onChangeText={setQuery}
              placeholder="Search by name, type, model…"
              placeholderTextColor={Palette.inkFaint}
              autoCorrect={false}
              autoCapitalize="none"
              style={[styles.searchInput, webNoOutline]}
            />
            {query.length > 0 ? (
              <Pressable onPress={() => setQuery('')} hitSlop={8}>
                <X size={14} color={Palette.inkFaint} strokeWidth={2} />
              </Pressable>
            ) : null}
          </View>
        </View>

        {/* Filter chips: location + needs-tune + select-mode. Wraps on
            narrow screens (mobile portrait fits two rows comfortably);
            centered as a group so partial trailing rows don't read as
            left-aligned. Replaces the v1 horizontal-scroll layout —
            that was hiding chips off-screen on phones, and the user
            didn't have a visual cue that more chips existed beyond the
            edge bleed. */}
        <View style={styles.chipRow}>
          <FilterChip
            label="All"
            active={locFilter === 'all'}
            onPress={() => setLocFilter('all')}
          />
          <FilterChip
            label="Inside"
            active={locFilter === 'inside'}
            onPress={() => setLocFilter('inside')}
          />
          <FilterChip
            label="Outside"
            active={locFilter === 'outside'}
            onPress={() => setLocFilter('outside')}
          />
          <View style={styles.chipDivider} />
          <FilterChip
            label={totalTune > 0 ? `Needs tune · ${totalTune}` : 'Needs tune'}
            active={tuneOnly}
            disabled={totalTune === 0}
            onPress={() => setTuneOnly((v) => !v)}
            tone="honey"
          />
          <FilterChip
            label={selectMode ? 'Done' : 'Select'}
            active={selectMode}
            onPress={() => (selectMode ? exitSelect() : setSelectMode(true))}
            tone={selectMode ? 'forest' : 'neutral'}
          />
        </View>

        {win.total === 0 ? (
          <View style={styles.empty}>
            <Text style={styles.emptyTitle}>
              {all.length === 0 ? 'No systems yet' : 'Nothing matches'}
            </Text>
            <Text style={styles.emptyBody}>
              {all.length === 0
                ? 'Tap + to add your first water heater, fridge, or any other home system.'
                : 'Try a different search or clear the filters above.'}
            </Text>
          </View>
        ) : (
          <>
            {grouped.inside.length > 0 ? (
              <>
                <SectionHeader title="Inside" count={grouped.inside.length} />
                <View style={styles.list}>
                  {grouped.inside.map((s) => (
                    <SystemListRow
                      key={s.id}
                      system={s}
                      selectMode={selectMode}
                      selected={selected.has(s.id)}
                      needsTune={needsTuneIds.has(s.id)}
                      onPress={() => {
                        if (selectMode) toggleSelected(s.id);
                        else router.push(`/system/${s.id}` as never);
                      }}
                    />
                  ))}
                </View>
              </>
            ) : null}

            {grouped.outside.length > 0 ? (
              <>
                <SectionHeader title="Outside" count={grouped.outside.length} />
                <View style={styles.list}>
                  {grouped.outside.map((s) => (
                    <SystemListRow
                      key={s.id}
                      system={s}
                      selectMode={selectMode}
                      selected={selected.has(s.id)}
                      needsTune={needsTuneIds.has(s.id)}
                      onPress={() => {
                        if (selectMode) toggleSelected(s.id);
                        else router.push(`/system/${s.id}` as never);
                      }}
                    />
                  ))}
                </View>
              </>
            ) : null}
          </>
        )}

        <InfiniteFooter
          hasMore={win.hasMore}
          total={win.total}
          loadMore={win.loadMore}
          noun="system"
        />
      </ScrollView>

      {/* Floating bulk-action bar — only mounted when selectMode is on
          AND something is selected. Sits above the tab bar / bottom safe
          area so it can't be missed. */}
      {selectMode && selected.size > 0 ? (
        <View style={styles.bulkBar}>
          <Text style={styles.bulkText}>
            {selected.size} selected
          </Text>
          <Pressable
            onPress={onBulkDelete}
            disabled={busy}
            style={({ pressed }) => [
              styles.bulkBtn,
              pressed && { transform: [{ scale: 0.97 }] },
              busy && { opacity: 0.5 },
            ]}>
            <Trash2 size={15} color={Palette.cream} strokeWidth={2} />
            <Text style={styles.bulkBtnText}>{busy ? 'Working…' : 'Delete'}</Text>
          </Pressable>
        </View>
      ) : null}
    </SafeAreaView>
  );
}

function FilterChip({
  label,
  active,
  onPress,
  disabled,
  tone = 'neutral',
}: {
  label: string;
  active: boolean;
  onPress: () => void;
  disabled?: boolean;
  tone?: 'neutral' | 'honey' | 'forest';
}) {
  const activeBg =
    tone === 'honey'
      ? Palette.honeySoft
      : tone === 'forest'
      ? Palette.forest
      : '#DCE7E1';
  const activeBorder =
    tone === 'honey'
      ? 'rgba(225,163,61,0.5)'
      : tone === 'forest'
      ? Palette.forest
      : Palette.forestLight;
  const activeColor =
    tone === 'forest' ? Palette.cream : Palette.forest;
  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      style={({ pressed, hovered }) => [
        styles.fchip,
        webTransition,
        active && { backgroundColor: activeBg, borderColor: activeBorder },
        hovered && !active && { borderColor: Palette.forestLight },
        pressed && !disabled && { transform: [{ scale: 0.97 }] },
        disabled && { opacity: 0.4 },
      ]}>
      <Text
        style={[
          styles.fchipText,
          active && { color: activeColor },
        ]}>
        {label}
      </Text>
    </Pressable>
  );
}

function SectionHeader({ title, count }: { title: string; count: number }) {
  return (
    <View style={styles.sectionRow}>
      <Text style={styles.sectionTitle}>{title}</Text>
      <Text style={styles.sectionCount}>{count}</Text>
    </View>
  );
}

function SystemListRow({
  system,
  selectMode,
  selected,
  needsTune,
  onPress,
}: {
  system: SystemRow;
  selectMode: boolean;
  selected: boolean;
  needsTune: boolean;
  onPress: () => void;
}) {
  const display = resolveSystemDisplay(system);
  const subtype = getSubtypeMeta(system.kind, system.subtype);
  const Icon: ComponentType<{ size?: number; color?: string; strokeWidth?: number }> =
    resolveIcon(display.icon);

  const detailBits = [
    subtype?.label,
    system.installed_year ? `${system.installed_year}` : null,
    system.model,
  ].filter(Boolean) as string[];

  return (
    <Pressable
      accessibilityRole="button"
      onPress={onPress}
      style={({ pressed, hovered }) => [
        styles.row,
        webTransition,
        selected && styles.rowSelected,
        hovered && !selected && styles.rowHover,
        pressed && { transform: [{ scale: 0.99 }] },
      ]}>
      {selectMode ? (
        <View style={styles.checkbox}>
          {selected ? (
            <CheckSquare size={20} color={Palette.forest} strokeWidth={2} />
          ) : (
            <Square size={20} color={Palette.inkFaint} strokeWidth={2} />
          )}
        </View>
      ) : (
        <View style={styles.ico}>
          <Icon size={20} color={Palette.forest} strokeWidth={1.7} />
        </View>
      )}
      <View style={{ flex: 1, minWidth: 0 }}>
        <View style={styles.rowHead}>
          <Text style={styles.rowTitle} numberOfLines={1}>
            {system.nickname ?? display.label}
          </Text>
          {needsTune ? (
            <View style={styles.tuneBadge}>
              <Text style={styles.tuneBadgeText}>Needs tune</Text>
            </View>
          ) : null}
        </View>
        <Text style={styles.rowDetail} numberOfLines={1}>
          {detailBits.length > 0
            ? detailBits.join(' · ')
            : 'No details yet — tap to add.'}
        </Text>
      </View>
      {!selectMode ? (
        <ChevronRight size={14} color={Palette.inkFaint} strokeWidth={1.8} />
      ) : null}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Palette.sand },
  topbar: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: Spacing.four, paddingTop: Spacing.two, paddingBottom: Spacing.two,
  },
  back: {
    width: 36, height: 36, borderRadius: 18, borderWidth: 1,
    borderColor: Palette.line, backgroundColor: Palette.card,
    alignItems: 'center', justifyContent: 'center',
  },
  topbarTitle: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  addBtn: {
    width: 36, height: 36, borderRadius: 18,
    backgroundColor: Palette.forest,
    alignItems: 'center', justifyContent: 'center',
  },

  header: { paddingHorizontal: 22, paddingTop: Spacing.three },
  h1: {
    fontFamily: Fonts.serif, fontSize: 27, color: Palette.ink,
    letterSpacing: -0.3,
  },
  sub: {
    marginTop: 3, fontFamily: Fonts.sansMedium, fontSize: 13, color: Palette.inkSoft,
  },
  // "Tune N systems" CTA on the Systems page. Honey-soft fill + amber
  // border mirror the home-tab "tune" chip so the cue reads as the
  // same affordance across surfaces.
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 12,
  },
  tuneCta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 14, paddingVertical: 9,
    borderRadius: 999,
    borderWidth: 1.5,
    borderColor: 'rgba(225,163,61,0.45)',
    backgroundColor: Palette.honeySoft,
    alignSelf: 'flex-start',
  },
  tuneCtaText: {
    fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.honeyDeep,
    letterSpacing: 0.2,
  },

  searchWrap: {
    paddingHorizontal: Spacing.four, marginTop: Spacing.three,
  },
  searchInner: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    paddingHorizontal: 12,
    minHeight: 42,
    backgroundColor: Palette.card,
    borderWidth: 1, borderColor: Palette.line,
    borderRadius: 999,
  },
  searchInput: {
    flex: 1,
    fontFamily: Fonts.sansSemibold, fontSize: 13.5, color: Palette.ink,
  },

  // chipScroll kept for backwards-compat in case anything imports it,
  // but the chip row no longer uses a horizontal ScrollView. We switched
  // to a wrapping flex row so chips fit on narrow phones without
  // hiding behind an edge-bleed scroll.
  chipScroll: {
    marginTop: 10,
  },
  chipRow: {
    marginTop: 10,
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: Spacing.four,
  },
  chipDivider: {
    width: 1, height: 18, backgroundColor: Palette.line, marginHorizontal: 4,
  },
  fchip: {
    paddingHorizontal: 13, paddingVertical: 7,
    borderRadius: 999,
    borderWidth: 1.5, borderColor: Palette.line,
    backgroundColor: Palette.card,
  },
  fchipText: {
    fontFamily: Fonts.sansBold, fontSize: 13,
    color: Palette.inkSoft, letterSpacing: 0.2,
  },

  sectionRow: {
    flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between',
    paddingHorizontal: 22,
    marginTop: Spacing.four, marginBottom: Spacing.two,
  },
  sectionTitle: {
    fontFamily: Fonts.serifBold, fontSize: 16, color: Palette.ink,
    letterSpacing: -0.2,
  },
  sectionCount: {
    fontFamily: Fonts.sansBold, fontSize: 12, color: Palette.inkFaint,
    letterSpacing: 0.4,
  },

  list: { paddingHorizontal: 18, gap: 8 },
  row: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    backgroundColor: Palette.card,
    borderWidth: 1, borderColor: Palette.line,
    borderRadius: Radius.xl,
    padding: 13,
  },
  rowSelected: {
    backgroundColor: '#EAF0EA',
    borderColor: Palette.forestLight,
  },
  rowHover: {
    borderColor: Palette.forestLight,
    backgroundColor: '#FBF6EA',
  },
  ico: {
    width: 42, height: 42, borderRadius: 13,
    backgroundColor: '#EAF0EA',
    alignItems: 'center', justifyContent: 'center',
  },
  checkbox: {
    width: 42, height: 42, borderRadius: 13,
    alignItems: 'center', justifyContent: 'center',
  },
  rowHead: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
  },
  rowTitle: {
    flex: 1,
    fontFamily: Fonts.sansSemibold, fontSize: 14.5, color: Palette.ink,
    letterSpacing: -0.1,
  },
  tuneBadge: {
    paddingHorizontal: 8, paddingVertical: 2,
    borderRadius: 999,
    backgroundColor: Palette.honeySoft,
    borderWidth: 1, borderColor: 'rgba(225,163,61,0.45)',
  },
  tuneBadgeText: {
    fontFamily: Fonts.sansBold, fontSize: 11,
    color: Palette.honeyDeep, letterSpacing: 0.4,
  },
  rowDetail: {
    marginTop: 3,
    fontFamily: Fonts.sansMedium, fontSize: 13, color: Palette.inkSoft,
  },

  empty: {
    paddingHorizontal: 32, paddingVertical: 60,
    alignItems: 'center',
  },
  emptyTitle: {
    fontFamily: Fonts.serifBold, fontSize: 18, color: Palette.ink,
    marginBottom: 8,
  },
  emptyBody: {
    fontFamily: Fonts.sansMedium, fontSize: 13, color: Palette.inkSoft,
    textAlign: 'center', lineHeight: 20,
  },

  bulkBar: {
    position: 'absolute', left: 0, right: 0, bottom: 0,
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: Spacing.four, paddingVertical: 14,
    backgroundColor: Palette.card,
    borderTopWidth: 1, borderTopColor: Palette.line,
    shadowColor: '#000', shadowOpacity: 0.08, shadowRadius: 12,
    shadowOffset: { width: 0, height: -4 },
    elevation: 12,
  },
  bulkText: {
    fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.ink,
  },
  bulkBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 7,
    paddingVertical: 11, paddingHorizontal: 18,
    borderRadius: 999,
    backgroundColor: Palette.clay,
  },
  bulkBtnText: {
    fontFamily: Fonts.sansBold, fontSize: 13, color: Palette.cream,
    letterSpacing: 0.2,
  },
});
